import { config } from '@iheartjane/config';
import {
  Banner,
  Box,
  Button,
  Card,
  CheckIcon,
  CopyIcon,
  Flex,
  InfoIcon,
  Link,
  Modal,
  Typography,
  useToast,
} from '@iheartjane/reefer';
import { Form, useForm, useWatch } from '@iheartjane/reefer-hook-form';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';

import { useUpsertEmbeddedMenuRow } from '@jane/business-admin/data-access';
import type {
  BloomOperatorConfig,
  EmbeddedMenuRow,
  MenuRow,
} from '@jane/business-admin/types';
import {
  ModalNames,
  bloomFontFamilyFromGoogleFontName,
} from '@jane/business-admin/util';
import { useGoogleFonts } from '@jane/shared-ecomm/data-access';
import { loadFont } from '@jane/shared/util';

import { ConfirmWrapperWithTracking } from '../../../../ConfirmWrapperWithTracking';
import { ColorPicker } from '../../../../premium/ColorPicker';
import { FontPicker } from '../../../../premium/FontPicker';
import styles from './EmbeddedRowModal.module.css';
import { EmbeddedRowPreview } from './EmbeddedRowPreview';

const DEFAULT_TEXT_COLOR = '#000000';
const DEFAULT_ACCENT_COLOR = '#8E00E6';
const DEFAULT_BRAND_SPECIALS_ACCENT_COLOR = '#F7F7F7';
const DEFAULT_FONT = 'Open Sans';

interface EmbeddedRowModalProps {
  bloomMenus: BloomOperatorConfig[];
  closeModal: () => void;
  embeddedMenuRow?: EmbeddedMenuRow | null;
  menuRow: Pick<
    MenuRow,
    'custom_display_name' | 'id' | 'menu_row_type' | 'store_id'
  >;
  storeId: number;
}

// Fonts are held in the form as family-name strings; the GoogleFontFamily
// object is resolved only where needed (picker display, preview) and the
// BloomFontFamily is built from the name on submit.
type EmbeddedRowFormValues = Pick<EmbeddedMenuRow, 'colors' | 'header'> & {
  bloom_operator_config_id?: string;
  fonts: {
    body?: string;
    heading?: string;
  };
};

export const EmbeddedRowModal = ({
  bloomMenus,
  closeModal,
  embeddedMenuRow,
  menuRow,
  storeId,
}: EmbeddedRowModalProps) => {
  const toast = useToast();
  const isBrandSpecials = menuRow.menu_row_type === 'brand_specials';
  const isSpecials = menuRow.menu_row_type === 'specials';
  const modalContentRef = useRef<HTMLDivElement>(null);
  const [copiedField, setCopiedField] = useState<'script' | 'html' | null>(
    null
  );
  const [createdEmbeddedRow, setCreatedEmbeddedRow] =
    useState<EmbeddedMenuRow>();
  const { mutate: upsertEmbeddedRow, isPending } = useUpsertEmbeddedMenuRow(
    String(menuRow.store_id),
    menuRow.id!
  );

  const fontQuery = useGoogleFonts({ apiKey: config.googleFontsKey });
  const supportedFontFamilies = useMemo(
    () => fontQuery.data ?? [],
    [fontQuery.data]
  );

  // The default fonts and accent color come from the selected premium menu's
  // bloom theme. Text color is constant across menus.
  const getMenuTheme = useCallback(
    (menu: BloomOperatorConfig) => ({
      headingFont:
        menu.theme?.bloom?.fonts?.heading?.font_family || DEFAULT_FONT,
      bodyFont: menu.theme?.bloom?.fonts?.body?.font_family || DEFAULT_FONT,
      accentColor: isBrandSpecials
        ? DEFAULT_BRAND_SPECIALS_ACCENT_COLOR
        : menu.theme?.bloom?.colors?.deals?.main || DEFAULT_ACCENT_COLOR,
    }),
    [isBrandSpecials]
  );

  const colorsInfoLabel = useMemo(() => {
    if (isBrandSpecials) {
      return 'Since the embedded row background is transparent, make sure your text color stays readable on the background where you plan to place it.';
    }
    return `Since the embedded row background is transparent, make sure your text and ${isSpecials ? 'deal' : 'price'} colors stay readable on the background where you plan to place it.`;
  }, [isBrandSpecials, isSpecials]);

  const findSelectedBloomMenu = useCallback(
    (id: string): BloomOperatorConfig => {
      const menu = bloomMenus.find((menu) => menu.id === Number(id));
      if (!menu) {
        throw new Error(`No bloom menu found for id ${id}`);
      }
      return menu;
    },
    [bloomMenus]
  );

  const initialBloomMenu = embeddedMenuRow?.bloom_operator_config_id
    ? bloomMenus.find(
        ({ id }) => id === embeddedMenuRow.bloom_operator_config_id
      ) || bloomMenus[0]
    : bloomMenus[0];
  const initialDefaults = getMenuTheme(initialBloomMenu);
  const formMethods = useForm<EmbeddedRowFormValues>({
    defaultValues: {
      bloom_operator_config_id: initialBloomMenu.id.toString(),
      header:
        embeddedMenuRow?.header || menuRow.custom_display_name || undefined,
      colors: {
        text: embeddedMenuRow?.colors['text'] || DEFAULT_TEXT_COLOR,
        accent:
          embeddedMenuRow?.colors['accent'] || initialDefaults.accentColor,
      },
      fonts: {
        heading:
          embeddedMenuRow?.fonts.heading.font_family ||
          initialDefaults.headingFont,
        body:
          embeddedMenuRow?.fonts.body.font_family || initialDefaults.bodyFont,
      },
    },
  });

  const {
    control,
    formState: { isDirty, isValid },
    getValues,
    reset,
    setValue,
  } = formMethods;

  // local state, not useWatch: useWatch can return undefined before
  // defaultValues settle on first render
  const [selectedBloomMenuId, setSelectedBloomMenuId] = useState(
    initialBloomMenu.id.toString()
  );
  const selectedBloomMenu = findSelectedBloomMenu(selectedBloomMenuId);
  const menuDetails = getMenuTheme(selectedBloomMenu);

  const defaultHeadingFont = menuDetails.headingFont;
  const defaultBodyFont = menuDetails.bodyFont;
  const bloomAccentColor = menuDetails.accentColor;
  const menuUrl = selectedBloomMenu.menu_url;

  const bloomMenuOptions = useMemo(
    () =>
      bloomMenus.map((menu) => ({
        label: `${menu.operator.name} — ${menu.menu_url}`,
        value: menu.id.toString(),
      })),
    [bloomMenus]
  );

  // When a different premium menu is picked, swap fonts and accent color to
  // the new menu's bloom theme defaults — but only for values still at the
  // previous menu's defaults, so customizations survive the switch.
  const handleBloomMenuChange = useCallback(
    (value: string) => {
      const previousDefaults = getMenuTheme(
        findSelectedBloomMenu(selectedBloomMenuId)
      );
      const newDefaults = getMenuTheme(findSelectedBloomMenu(value));

      if (getValues('fonts.heading') === previousDefaults.headingFont) {
        setValue('fonts.heading', newDefaults.headingFont, {
          shouldDirty: true,
        });
      }

      if (getValues('fonts.body') === previousDefaults.bodyFont) {
        setValue('fonts.body', newDefaults.bodyFont, { shouldDirty: true });
      }

      if (getValues('colors.accent') === previousDefaults.accentColor) {
        setValue('colors.accent', newDefaults.accentColor, {
          shouldDirty: true,
        });
      }

      setSelectedBloomMenuId(value);
    },
    [
      findSelectedBloomMenu,
      getMenuTheme,
      getValues,
      selectedBloomMenuId,
      setValue,
    ]
  );

  const fontUpdates = useWatch({ control, name: 'fonts' });

  // Resolve the GoogleFontFamily by name to load its file for live preview.
  // No-ops until the font list has loaded.
  useEffect(() => {
    const headingFont = supportedFontFamilies.find(
      ({ family }) => family === fontUpdates.heading
    );
    const bodyFont = supportedFontFamilies.find(
      ({ family }) => family === fontUpdates.body
    );

    loadFont({
      fontFamily: headingFont?.family,
      url: headingFont?.files['regular'],
    });
    loadFont({
      fontFamily: bodyFont?.family,
      url: bodyFont?.files['regular'],
    });
  }, [fontUpdates.heading, fontUpdates.body, supportedFontFamilies]);

  const onSubmit = (data: EmbeddedRowFormValues) => {
    const headingFont = bloomFontFamilyFromGoogleFontName(data.fonts.heading);
    const bodyFont = bloomFontFamilyFromGoogleFontName(data.fonts.body);
    const finalData = {
      ...data,
      bloom_operator_config_id: Number(data.bloom_operator_config_id),
      fonts: {
        heading: headingFont ?? {
          font_family: undefined,
          url: undefined,
        },
        body: bodyFont ?? {
          font_family: undefined,
          url: undefined,
        },
      },
    };
    upsertEmbeddedRow(finalData, {
      onSuccess: (response) => {
        reset(getValues());
        toast.add({
          label: embeddedMenuRow
            ? 'Embedded row updated successfully'
            : 'Embedded row created successfully',
          variant: 'success',
        });
        if (embeddedMenuRow) {
          closeModal();
        } else {
          setCreatedEmbeddedRow(response.embedded_menu_row);
          /**
           * If this is a new embedded row, scroll to top of
           * modal so they can see the embed code
           */
          modalContentRef.current?.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }
      },
      onError: () => {
        toast.add({
          label: embeddedMenuRow
            ? 'Error updating embedded row'
            : 'Error creating embedded row',
          variant: 'error',
        });
      },
    });
  };

  const activeEmbeddedRow = createdEmbeddedRow || embeddedMenuRow;
  const scriptCode = `<script type="module" src="${config.embeddedMenuRowScriptUrl}"></script>`;
  const htmlCode = `<jane-menu-row row-id="${activeEmbeddedRow?.id}" />`;

  const handleCopyScript = useCallback(() => {
    navigator.clipboard.writeText(scriptCode);
    setCopiedField('script');
    setTimeout(() => setCopiedField(null), 2000);
  }, [scriptCode]);

  const handleCopyHtml = useCallback(() => {
    navigator.clipboard.writeText(htmlCode);
    setCopiedField('html');
    setTimeout(() => setCopiedField(null), 2000);
  }, [htmlCode]);

  const restoreDefaultFonts = useCallback(() => {
    setValue('fonts.heading', defaultHeadingFont, { shouldDirty: true });
    setValue('fonts.body', defaultBodyFont, { shouldDirty: true });
  }, [defaultHeadingFont, defaultBodyFont, setValue]);

  const [previewOpen, setPreviewOpen] = useState(false);

  const restoreDefaultColors = useCallback(() => {
    setValue('colors.text', DEFAULT_TEXT_COLOR, {
      shouldDirty: true,
    });
    setValue('colors.accent', bloomAccentColor, {
      shouldDirty: true,
    });
  }, [bloomAccentColor, setValue]);

  return (
    <ConfirmWrapperWithTracking
      open
      setOpen={closeModal}
      variant="full-screen"
      hasChanges={isDirty}
      modalName={ModalNames.EmbeddedMenuRow}
    >
      <Form.BaseForm
        formErrorName="embed-row-errors"
        formMethods={formMethods}
        name="embedded row modal"
        onSubmit={onSubmit}
      >
        <Modal.Header
          title={activeEmbeddedRow ? 'Edit embedded row' : 'Create embed code'}
          actions={
            <>
              <Button
                variant="secondary"
                label="Preview"
                ml={16}
                onClick={() => setPreviewOpen(true)}
                disabled={!isValid}
              />
              <Form.SubmitButton
                loading={isPending}
                disabled={!isValid || activeEmbeddedRow ? !isDirty : false}
                label={activeEmbeddedRow ? 'Publish' : 'Create embed code'}
                ml={16}
                variant="primary"
              />
            </>
          }
        />
        <Modal.Content ref={modalContentRef}>
          <Flex flexDirection="column" gap={24} maxWidth="944px" mx="auto">
            {activeEmbeddedRow && (
              <Card width="100%">
                <Card.Content p={40}>
                  <Typography branded variant="header-bold">
                    Embed this row on your website
                  </Typography>
                  <Typography color="grays-mid">
                    Last published on{' '}
                    {new Date(activeEmbeddedRow.updated_at).toLocaleString()}
                  </Typography>

                  <Flex
                    justifyContent="space-between"
                    alignItems="center"
                    mt={24}
                  >
                    <Typography variant="body-bold">
                      Rendering script
                    </Typography>
                    <Flex alignItems="center" gap={4}>
                      {copiedField === 'script' ? (
                        <CheckIcon size="md" />
                      ) : (
                        <CopyIcon
                          size="md"
                          altText="Copy rendering script"
                          onClick={handleCopyScript}
                        />
                      )}
                      <Link onClick={handleCopyScript}>Copy code</Link>
                    </Flex>
                  </Flex>
                  <Box
                    background="grays-ultralight"
                    border="grays-light"
                    borderRadius="sm"
                    my={12}
                    px={16}
                    py={12}
                    width="100%"
                  >
                    <Typography variant="body" as="span" whiteSpace="nowrap">
                      {scriptCode}
                    </Typography>
                  </Box>
                  <Typography color="grays-mid" mt={8}>
                    You only need to add this script once per page. If
                    you&apos;ve already embedded another row, you don&apos;t
                    need to add it again.
                  </Typography>

                  <Flex
                    justifyContent="space-between"
                    alignItems="center"
                    mt={24}
                  >
                    <Typography variant="body-bold">
                      Embedded row HTML
                    </Typography>
                    <Flex alignItems="center" gap={4}>
                      {copiedField === 'html' ? (
                        <CheckIcon size="md" />
                      ) : (
                        <CopyIcon
                          size="md"
                          altText="Copy embedded row HTML"
                          onClick={handleCopyHtml}
                        />
                      )}
                      <Link onClick={handleCopyHtml}>Copy code</Link>
                    </Flex>
                  </Flex>
                  <Box
                    background="grays-ultralight"
                    border="grays-light"
                    borderRadius="sm"
                    my={12}
                    px={16}
                    py={12}
                    width="100%"
                  >
                    <Typography variant="body" as="span" whiteSpace="nowrap">
                      {htmlCode}
                    </Typography>
                  </Box>
                  <Typography color="grays-mid" mt={8}>
                    Copy and place this HTML code for your{' '}
                    {activeEmbeddedRow.header} row.
                  </Typography>
                </Card.Content>
              </Card>
            )}

            <Card width="100%">
              <Card.Content p={40}>
                <Typography branded variant="header-bold">
                  Embedded row
                </Typography>

                <Typography>
                  Embed this row directly on your website using an embed code.
                  Product tiles are clickable entry points to their product
                  detail pages, but add-to-cart functionality is limited.
                </Typography>

                {bloomMenus.length > 1 && (
                  <Form.SelectField
                    mt={24}
                    label="Premium menu"
                    name="bloom_operator_config_id"
                    helperText="Choose which premium menu this row is associated with."
                    onChange={handleBloomMenuChange}
                    options={bloomMenuOptions}
                    required={true}
                    width="100%"
                  />
                )}

                {isBrandSpecials ? (
                  <Box mt={24}>
                    <Typography variant="body-bold" mb={4}>
                      Header
                    </Typography>
                    <Typography>{menuRow.custom_display_name}</Typography>
                  </Box>
                ) : (
                  <Form.TextField
                    mt={24}
                    label="Header"
                    name="header"
                    helperText="The name you've set on your menu will display on your website—override it by adjusting here."
                  />
                )}
              </Card.Content>
            </Card>

            <Card width="100%" className={styles['font-card']}>
              <Card.Content p={40} className={styles['font-card-content']}>
                <Flex justifyContent="space-between">
                  <Flex alignItems="center">
                    <Typography branded variant="header-bold">
                      Fonts
                    </Typography>
                  </Flex>
                  <Link onClick={restoreDefaultFonts}>Restore default</Link>
                </Flex>

                <Typography>
                  Fonts from your font admin are used by default. You can
                  override by adjusting them here.
                </Typography>

                <Flex gap={24} mt={24} width="100%">
                  <Flex flexDirection="column" gap={8} width="50%">
                    <Typography variant="body-bold">Heading</Typography>
                    <Controller
                      control={control}
                      name="fonts.heading"
                      render={({ field }) => (
                        <FontPicker
                          activeFontFamily={
                            field.value
                              ? { label: field.value, value: field.value }
                              : null
                          }
                          defaultFontFamily={{
                            label: defaultHeadingFont,
                            value: defaultHeadingFont,
                          }}
                          fontFamilies={supportedFontFamilies}
                          isLayered
                          label="heading-font"
                          maxHeight={320}
                          onChange={(font) => {
                            field.onChange(font?.family);
                          }}
                        />
                      )}
                    />
                  </Flex>

                  <Flex flexDirection="column" gap={8} width="50%">
                    <Typography variant="body-bold">Paragraph</Typography>
                    <Controller
                      control={control}
                      name="fonts.body"
                      render={({ field }) => (
                        <FontPicker
                          activeFontFamily={
                            field.value
                              ? { label: field.value, value: field.value }
                              : null
                          }
                          defaultFontFamily={{
                            label: defaultBodyFont,
                            value: defaultBodyFont,
                          }}
                          fontFamilies={supportedFontFamilies}
                          isLayered
                          label="body-font"
                          maxHeight={320}
                          onChange={(font) => {
                            field.onChange(font?.family);
                          }}
                        />
                      )}
                    />
                  </Flex>
                </Flex>
              </Card.Content>
            </Card>

            <Card width="100%">
              <Card.Content p={40}>
                <Flex justifyContent="space-between">
                  <Flex alignItems="center">
                    <Typography branded variant="header-bold">
                      Colors
                    </Typography>
                  </Flex>
                  <Link onClick={restoreDefaultColors}>Restore default</Link>
                </Flex>

                {!isBrandSpecials && (
                  <Typography>
                    Your brand colors are applied by default, and you can
                    override by updating swatches here.
                  </Typography>
                )}

                <Flex mt={24}>
                  <Controller
                    name="colors.text"
                    control={control}
                    render={({ field }) => (
                      <ColorPicker
                        label="Text"
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        shouldStretch
                      />
                    )}
                  />
                  <Controller
                    name="colors.accent"
                    control={control}
                    render={({ field }) => (
                      <ColorPicker
                        label={
                          isSpecials
                            ? 'Deal'
                            : isBrandSpecials
                              ? 'Background'
                              : 'Price'
                        }
                        mr={0}
                        onChange={field.onChange}
                        shouldStretch
                        value={field.value ?? ''}
                      />
                    )}
                  />
                </Flex>

                <Banner
                  full
                  icon={<InfoIcon />}
                  label={colorsInfoLabel}
                  mt={24}
                  variant="info"
                />
              </Card.Content>
            </Card>
          </Flex>
        </Modal.Content>
        {previewOpen && (
          <EmbeddedRowPreview
            menuRowId={menuRow.id!}
            menuRowType={menuRow.menu_row_type!}
            menuUrl={menuUrl}
            onClose={() => setPreviewOpen(false)}
            open={previewOpen}
            storeId={storeId}
          />
        )}
      </Form.BaseForm>
    </ConfirmWrapperWithTracking>
  );
};
